/**
 * Text Chunking Utilities for TTS Processing
 *
 * Handles splitting long text into manageable chunks for text-to-speech
 * systems with size limitations (e.g., 5000 character limit).
 */

/**
 * Chunk text for TTS processing while preserving sentence boundaries
 * @param {string} text - Text to chunk
 * @param {number} maxChunkSize - Maximum size per chunk (default 4500 to leave buffer)
 * @returns {string[]} Array of text chunks
 */
export const chunkTextForTTS = (text, maxChunkSize = 4500) => {
  if (!text || text.length <= maxChunkSize) {
    return [text];
  }

  // Split by sentences (handles . ! ? with proper spacing)
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  const chunks = [];
  let currentChunk = '';

  for (const sentence of sentences) {
    const trimmedSentence = sentence.trim();

    // If single sentence exceeds max, split by words
    if (trimmedSentence.length > maxChunkSize) {
      if (currentChunk) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }

      // Split long sentence by words
      const words = trimmedSentence.split(/\s+/);
      let wordChunk = '';
      for (const word of words) {
        if ((wordChunk + ' ' + word).length > maxChunkSize) {
          if (wordChunk) chunks.push(wordChunk.trim());
          wordChunk = word;
        } else {
          wordChunk += (wordChunk ? ' ' : '') + word;
        }
      }
      if (wordChunk) chunks.push(wordChunk.trim());

    } else if ((currentChunk + ' ' + trimmedSentence).length > maxChunkSize) {
      // Current chunk + new sentence would exceed limit
      if (currentChunk) chunks.push(currentChunk.trim());
      currentChunk = trimmedSentence;
    } else {
      // Add sentence to current chunk
      currentChunk += (currentChunk ? ' ' : '') + trimmedSentence;
    }
  }

  if (currentChunk) {
    chunks.push(currentChunk.trim());
  }

  return chunks.filter(chunk => chunk.length > 0);
};

/**
 * Check if text needs chunking
 * @param {string} text - Text to check
 * @param {number} maxSize - Maximum allowed size (default 5000)
 * @returns {boolean} True if text exceeds maxSize
 */
export const needsChunking = (text, maxSize = 5000) => {
  return text && text.length > maxSize;
};

/**
 * Get chunk info for display purposes
 * @param {string} text - Original text
 * @param {number} maxChunkSize - Maximum chunk size
 * @returns {Object} Chunk information { totalChunks, averageChunkSize, needsChunking }
 */
export const getChunkInfo = (text, maxChunkSize = 4500) => {
  if (!text) {
    return { totalChunks: 0, averageChunkSize: 0, needsChunking: false };
  }

  const chunks = chunkTextForTTS(text, maxChunkSize);
  const totalChunks = chunks.length;
  const averageChunkSize = totalChunks > 0
    ? Math.round(chunks.reduce((sum, chunk) => sum + chunk.length, 0) / totalChunks)
    : 0;

  return {
    totalChunks,
    averageChunkSize,
    needsChunking: totalChunks > 1
  };
};

export default {
  chunkTextForTTS,
  needsChunking,
  getChunkInfo
};
